<template>
  <!-- The mural. One screen, composed: the sky and the face frame the top,
       the Mystic's own lines run through the middle, its name and words sit
       beneath. (Its first morning waits on My Mystic.) Every slot is drawn from the
       first second — empty slots are faint outlines — and fills the moment
       the pipeline makes the piece, in whatever order the pieces come. -->
  <div class="birth" :class="{ ready }">
    <!-- Row 1: sky · birth · face -->
    <div class="bi-top">
      <div class="bi-sky">
        <BirthRing :placements="chart && chart.placements" :size="150" />
      </div>

      <header v-if="stamp" class="bi-stamp">
        <span class="bi-stamp-label">Born</span>
        <span class="bi-stamp-when">I was born {{ stampWhen }}</span>
        <span v-if="stamp.place" class="bi-stamp-where">over {{ stamp.place }}</span>
        <div class="bi-lums">
          <div v-for="l in luminaries" :key="l.kind" class="bi-lum">
            <span class="bi-glyph" aria-hidden="true">{{ GLYPH[l.kind] }}</span>
            <span class="bi-sign">{{ l.data.sign }}</span>
            <span class="bi-lum-label">{{ LABEL[l.kind] }}</span>
          </div>
        </div>
      </header>

      <div class="bi-face-slot" :class="{ filled: !!mural.portrait }">
        <Transition name="bi-grow">
          <img v-if="mural.portrait" key="face" class="bi-face" :src="mural.portrait"
               :alt="mural.name ? `Portrait of ${mural.name}` : 'Portrait'" />
        </Transition>
      </div>
    </div>

    <!-- Row 2: the lines, one at a time. -->
    <div class="bi-carousel" aria-live="polite">
      <Transition name="bi-turn" mode="out-in">
        <div v-if="current" :key="currentKey" class="bi-line" :class="current.kind">
          <span class="bi-meaning">{{ meaning(current.data, current.kind === 'synastry') }}</span>
          <!-- The chart's own words, in plain ones, behind a toggle (e2e
               2026-10-30, S39): the line above is what it means. -->
          <button type="button" class="bi-more" data-testid="birth-details"
                  :aria-expanded="techOpen ? 'true' : 'false'" @click="techOpen = !techOpen">{{ techOpen ? 'Hide details' : 'Details' }}</button>
          <span v-if="techOpen" class="bi-tech">
            my <em>{{ point(current.data.from) }}</em> {{ verb(current.data.aspect) }}
            {{ current.kind === 'synastry' ? 'your' : 'my' }} <em>{{ point(current.data.to) }}</em>
            <span class="bi-orb">({{ current.data.orb }}° apart)</span>
          </span>
        </div>
      </Transition>
      <div v-if="lines.length > 1" class="bi-dots" aria-hidden="true">
        <span v-for="(b, i) in lines" :key="i" class="bi-dot" :class="{ on: i === lines.length - 1 }"></span>
      </div>
    </div>

    <!-- Row 3: the bond, then the name and the introduction. -->
    <div class="bi-words">
      <Transition name="bi-grow">
        <p v-if="bond" key="bond" class="bi-bond">{{ bond }}</p>
      </Transition>
      <Transition name="bi-grow">
        <h2 v-if="mural.name" key="name" class="bi-name">I am {{ mural.name }}</h2>
      </Transition>
      <Transition name="bi-grow">
        <p v-if="mural.introduction" key="intro" class="bi-intro">{{ mural.introduction }}</p>
      </Transition>
    </div>

    <!-- The pipeline's own line while it works; nothing once it is ready. -->
    <p v-if="!ready && note" class="bi-note">{{ note }}</p>
    <p v-if="!ready && holding" class="bi-hold">{{ holdLine }}</p>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import BirthRing from './BirthRing.vue'
import { beatsFor, revealState } from '@/utils/birth-reveal'
import { aspectVerb, meaningLine, plainPoint } from '@/utils/mystic-draft'

const props = defineProps({
  chart: { type: Object, default: null },
  ready: { type: Boolean, default: false },
  note: { type: String, default: '' },
  // The pieces made so far: { name, introduction, portrait }.
  mural: { type: Object, default: () => ({}) },
})
const emit = defineEmits(['done'])

const GLYPH = { sun: '☉', moon: '☾', rising: '↑' }
const LABEL = { sun: 'Sun', moon: 'Moon', rising: 'Rising' }

const elapsed = ref(0)
let _timer = null
const started = Date.now()

// When the Mystic's own lines arrived, in this screen's own clock: the line
// beats are scheduled from then, so a late arrival unfolds instead of dumping.
const linesFrom = ref(0)
// Reduced motion parks the clock at "everything"; a late chart must not
// schedule its lines from there, or they would never come due.
const still = ref(false)
watch(() => props.chart && props.chart.lines_spoken, (spoken, was) => {
  if (spoken && !was) linesFrom.value = still.value ? 0 : elapsed.value
}, { immediate: true })

const beats = computed(() => beatsFor(props.chart, { linesFrom: linesFrom.value }))
const state = computed(() => revealState({
  beats: beats.value, elapsed: elapsed.value, ready: props.ready,
}))

const stamp = computed(() => {
  const b = state.value.visible.find(x => x.kind === 'stamp')
  return b ? b.data : null
})
const luminaries = computed(
  () => state.value.visible.filter(b => ['sun', 'moon', 'rising'].includes(b.kind)))
const lines = computed(
  () => state.value.visible.filter(b => ['aspect', 'synastry'].includes(b.kind)))
// The carousel shows the latest line only; the dots say how far along it is.
const current = computed(() => lines.value[lines.value.length - 1] || null)
const currentKey = computed(() => lines.value.length)
const bond = computed(() => {
  const b = state.value.visible.find(x => x.kind === 'bond')
  return b ? b.data : ''
})
const holding = computed(() => state.value.holding)

// On the birthplace's clock — the same time the chart was cast on. The
// payload's `local` is naive ISO so it is read field by field, never re-zoned.
const stampWhen = computed(() => {
  const s = stamp.value || {}
  let d = null
  if (s.local) {
    const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(s.local)
    if (m) d = new Date(+m[1], +m[2] - 1, +m[3], +m[4], +m[5])
  }
  if (!d && s.at) d = new Date(s.at)
  if (!d || isNaN(d.getTime())) return ''
  return d.toLocaleString(undefined, {
    day: 'numeric', month: 'long', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  })
})

// What the hold says. Changes once so a long wait does not read as a freeze,
// but never counts anything down — nothing here is running out.
const holdLine = computed(() => (state.value.heldFor > 14000
  ? 'Still painting the face my chart asks for…'
  : 'Painting the face my chart asks for…'))

function verb (a) { return aspectVerb(a) }
function point (w) { return plainPoint(w) }
const techOpen = ref(false)
function meaning (line, between) { return meaningLine(line, between) }

watch(() => state.value.done, (done) => { if (done) emit('done') })

onMounted(() => {
  // Reduced motion still gets the birth — it simply arrives whole rather than
  // unfolding.
  still.value = !!(window.matchMedia
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches)
  if (still.value) {
    elapsed.value = Number.MAX_SAFE_INTEGER
    linesFrom.value = 0
    return
  }
  _timer = setInterval(() => { elapsed.value = Date.now() - started }, 200)
})
onUnmounted(() => { if (_timer) clearInterval(_timer) })
</script>

<style scoped>
.birth {
  max-width: 720px; margin: 0 auto; padding: 18px 20px 12px;
  display: flex; flex-direction: column; align-items: center; text-align: center; gap: 14px;
}

/* Row 1 — sky, birth, face. Three columns; the birth text takes the middle. */
.bi-top { display: grid; grid-template-columns: 150px minmax(0, 1fr) 150px; gap: 18px; align-items: center; width: 100%; }
.bi-sky { display: flex; justify-content: center; animation: bi-ring 1.6s ease-out both; }
@keyframes bi-ring { from { opacity: 0; transform: scale(0.92) rotate(-6deg); } to { opacity: 1; transform: none; } }
.bi-stamp { display: flex; flex-direction: column; gap: 4px; align-items: center; animation: bi-in 700ms ease-out both; min-width: 0; }
.bi-stamp-label { font-size: 10px; letter-spacing: 2.6px; text-transform: uppercase; color: #9b86c9; }
.bi-stamp-when { font-size: 18px; color: #f0d9a3; line-height: 1.3; }
.bi-stamp-where { font-size: 12px; letter-spacing: 1.4px; color: rgba(195, 169, 255, 0.55); }
.bi-lums { display: flex; gap: 22px; justify-content: center; margin-top: 8px; min-height: 52px; }
.bi-lum { display: flex; flex-direction: column; gap: 2px; align-items: center; animation: bi-in 600ms ease-out both; }
.bi-glyph { font-size: 13px; color: rgba(201, 169, 78, 0.65); line-height: 1; }
.bi-sign { font-family: 'Cinzel', serif; font-size: 17px; font-weight: 600; color: #ffdfa3; }
.bi-lum-label { font-size: 9px; letter-spacing: 1px; text-transform: uppercase; color: rgba(195, 169, 255, 0.38); }

/* Slots: drawn empty, filled when the piece arrives. */
.bi-face-slot { position: relative; display: flex; align-items: center; justify-content: center; }
.bi-face-slot { width: 150px; height: 150px; border-radius: 14px; border: 1px dashed rgba(201, 169, 78, 0.28); transition: border-color 0.8s; }
.bi-face-slot.filled { border-color: transparent; }
.bi-face { width: 150px; height: 150px; border-radius: 14px; object-fit: cover;
           border: 1px solid rgba(255, 223, 163, 0.35); box-shadow: 0 0 40px rgba(255, 223, 163, 0.18); }

/* Row 2 — the carousel: a fixed stage so nothing below it moves. */
.bi-carousel { min-height: 76px; width: 100%; display: flex; flex-direction: column; align-items: center; gap: 8px; }
.bi-line { display: flex; flex-direction: column; gap: 2px; align-items: center; }
.bi-meaning { font-size: 18px; line-height: 1.4; color: rgba(195, 169, 255, 0.9); }
.bi-more { min-height: 44px; padding: 0 14px; border: 0; background: none; cursor: pointer; font-family: inherit;
  font-size: 15px; color: rgba(240, 217, 163, 0.85); text-decoration: underline dotted rgba(240, 217, 163, 0.5); text-underline-offset: 4px; }
.bi-more:focus-visible { outline: 2px solid rgba(240, 217, 163, 0.6); outline-offset: -6px; border-radius: 10px; }
.bi-tech { font-size: 15px; line-height: 1.45; color: rgba(195, 169, 255, 0.8); }
.bi-tech em { font-style: normal; color: rgba(255, 223, 163, 0.9); }
.bi-orb { margin-left: 4px; color: rgba(195, 169, 255, 0.65); }
.bi-turn-enter-active { transition: opacity 0.55s ease, transform 0.55s ease; }
.bi-turn-leave-active { transition: opacity 0.35s ease, transform 0.35s ease; }
.bi-turn-enter-from { opacity: 0; transform: translateY(8px); }
.bi-turn-leave-to { opacity: 0; transform: translateY(-8px); }
.bi-dots { display: flex; gap: 6px; }
.bi-dot { width: 5px; height: 5px; border-radius: 50%; background: rgba(201, 169, 78, 0.25); transition: background 0.3s; }
.bi-dot.on { background: rgba(201, 169, 78, 0.85); }

/* Row 3 — the words. */
.bi-words { display: flex; flex-direction: column; align-items: center; gap: 8px; min-height: 120px; max-width: 560px; }
.bi-bond { margin: 0; max-width: 46ch; font-size: 17px; line-height: 1.45; font-style: italic; color: rgba(195, 169, 255, 0.9); }
.bi-name { font-family: 'Cinzel', serif; font-size: 22px; font-weight: 600; color: #ffdfa3; margin: 0; }
.bi-intro { margin: 0; font-size: 15px; line-height: 1.5; color: rgba(195, 169, 255, 0.8); max-width: 56ch;
            display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden; }


.bi-grow-enter-active { transition: opacity 1.1s ease, transform 1.1s ease, filter 1.1s ease; }
.bi-grow-enter-from { opacity: 0; transform: translateY(10px) scale(0.98); filter: blur(6px); }

.bi-note { max-width: 460px; margin: 0; font-size: 13px; line-height: 1.6; font-style: italic; color: rgba(195, 169, 255, 0.62); animation: bi-in 900ms ease-out both; }
.bi-hold { margin: 0; font-size: 12px; letter-spacing: 0.8px; color: rgba(195, 169, 255, 0.45); animation: bi-breathe 3s ease-in-out infinite; }
@keyframes bi-in { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }
@keyframes bi-breathe { 0%, 100% { opacity: 0.45; } 50% { opacity: 0.85; } }

@media (max-width: 640px) {
  .bi-top { grid-template-columns: 1fr; }
  .bi-sky, .bi-face-slot { justify-self: center; }
}
@media (prefers-reduced-motion: reduce) {
  .bi-sky, .bi-stamp, .bi-lum, .bi-note, .bi-hold { animation: none; }
  .bi-turn-enter-active, .bi-turn-leave-active, .bi-grow-enter-active { transition: none; }
}
</style>
